import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

/**
 * CookieConsent — compact, low-footprint version matching CheckoutDM's
 * brand system (bg-ink/text-cream, sunset-gradient CTA, rounded corners).
 *
 * Design goals for this version:
 *  - Thin bar on desktop (single row, hugs the bottom, doesn't block content)
 *  - Even shorter on mobile — the collapsed banner is 2 short lines max so
 *    it doesn't eat screen space where most traffic comes from
 *  - Self-expires after 6 months: the choice is re-asked automatically once
 *    EXPIRY_MS has passed since it was saved, no manual version bump needed
 *
 * USAGE: render once in App.js, outside <Routes> so it persists across
 * every page:
 *   <CookieConsent />
 *
 * Check consent before firing any analytics/marketing script elsewhere:
 *   import { getCookieConsent } from "@/components/CookieConsent";
 *   const consent = getCookieConsent();
 *   if (consent?.analytics) { /* init analytics *\/ }
 */

const STORAGE_KEY = "checkoutdm_cookie_consent";
const CONSENT_VERSION = 2; // bump if you add new cookies/trackers later
const EXPIRY_MS = 180 * 24 * 60 * 60 * 1000; // 6 months — re-ask after this

export function getCookieConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.version !== CONSENT_VERSION) return null;
    if (!parsed.timestamp || Date.now() - parsed.timestamp > EXPIRY_MS) {
      localStorage.removeItem(STORAGE_KEY); // self-expired, ask again
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function saveCookieConsent(choices) {
  const payload = {
    version: CONSENT_VERSION,
    timestamp: Date.now(),
    necessary: true,
    analytics: choices.analytics,
    marketing: choices.marketing,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  return payload;
}

export default function CookieConsent({ onConsentChange }) {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: true, marketing: false });

  useEffect(() => {
    const existing = getCookieConsent();
    if (!existing) setVisible(true);
    else onConsentChange?.(existing);
  }, [onConsentChange]);

  const handleChoice = (choices) => {
    const saved = saveCookieConsent(choices);
    setVisible(false);
    onConsentChange?.(saved);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[9999] flex justify-center px-2 pb-2 sm:px-4 sm:pb-3">
      <div
        className={`w-full max-w-xl rounded-2xl border border-white/10 bg-ink text-cream shadow-2xl sm:max-w-2xl ${
          expanded ? "sm:rounded-3xl" : "sm:rounded-full"
        }`}
      >
        {!expanded ? (
          <div className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:gap-4 sm:py-2.5 sm:pl-5 sm:pr-2">
            <p className="text-xs leading-snug text-cream/80 sm:flex-1 sm:text-[13px]">
              <span className="mr-1">🍪</span>
              We use cookies to run your dashboard and, if you allow it, to
              improve CheckoutDM.{" "}
              <Link
                to="/privacy-policy"
                className="underline decoration-hotpink underline-offset-2 hover:text-hotpink"
              >
                Privacy Policy
              </Link>
            </p>

            <div className="flex items-center justify-end gap-2 sm:shrink-0">
              <button
                onClick={() => setExpanded(true)}
                className="inline-flex items-center justify-center rounded-full px-3 py-1.5 text-xs font-semibold text-cream/70 hover:text-cream"
              >
                Manage
              </button>
              <button
                onClick={() => handleChoice({ analytics: false, marketing: false })}
                className="inline-flex items-center justify-center rounded-full border border-white/20 px-3 py-1.5 text-xs font-semibold text-cream hover:bg-white/10"
              >
                Reject
              </button>
              <button
                onClick={() => handleChoice({ analytics: true, marketing: true })}
                className="inline-flex items-center justify-center rounded-full bg-sunset-gradient px-4 py-1.5 text-xs font-bold text-white shadow-lg hover:opacity-90"
              >
                Accept all
              </button>
            </div>
          </div>
        ) : (
          <div className="p-4 sm:p-5">
            <p className="font-display text-base font-black leading-tight">
              Cookie preferences
            </p>

            <div className="mt-3 space-y-2.5 divide-y divide-white/10">
              <div className="flex items-start justify-between gap-4 pt-2.5 first:pt-0">
                <div>
                  <p className="text-xs font-bold">Strictly necessary</p>
                  <p className="mt-0.5 text-[11px] text-cream/60">
                    Keeps you logged in and your dashboard functional.
                    Always on.
                  </p>
                </div>
                <span className="mt-0.5 h-2 w-2 shrink-0 rounded-full bg-mint" />
              </div>

              <div className="flex items-start justify-between gap-4 pt-2.5">
                <div>
                  <p className="text-xs font-bold">Analytics</p>
                  <p className="mt-0.5 text-[11px] text-cream/60">
                    Helps us see how the site and dashboard are used.
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={prefs.analytics}
                  onChange={(e) =>
                    setPrefs((p) => ({ ...p, analytics: e.target.checked }))
                  }
                  className="mt-0.5 h-4 w-4 shrink-0 accent-hotpink"
                />
              </div>

              <div className="flex items-start justify-between gap-4 pt-2.5">
                <div>
                  <p className="text-xs font-bold">Marketing</p>
                  <p className="mt-0.5 text-[11px] text-cream/60">
                    Used to personalize offers and measure campaigns.
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={prefs.marketing}
                  onChange={(e) =>
                    setPrefs((p) => ({ ...p, marketing: e.target.checked }))
                  }
                  className="mt-0.5 h-4 w-4 shrink-0 accent-hotpink"
                />
              </div>
            </div>

            <div className="mt-4 flex flex-row-reverse justify-end gap-2 sm:justify-end">
              <button
                onClick={() => handleChoice(prefs)}
                className="inline-flex items-center justify-center rounded-full bg-sunset-gradient px-4 py-1.5 text-xs font-bold text-white shadow-lg hover:opacity-90"
              >
                Save preferences
              </button>
              <button
                onClick={() => setExpanded(false)}
                className="inline-flex items-center justify-center rounded-full px-4 py-1.5 text-xs font-semibold text-cream/70 hover:text-cream"
              >
                Back
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
